/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-get, ember/no-runloop, ember/require-computed-property-dependencies, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
import { isArray } from '@ember/array';
import EmberObject, { get, action, computed } from '@ember/object';
import { or, alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { next } from '@ember/runloop';
import { isEmpty } from '@ember/utils';
import config from 'linced-app/config/environment';
import moment from 'moment';

// Internal Imports
import AttendanceViewMode from '@linc-technologies/linced-common/enums/person-option-attendance-view-mode';
import EnrolmentStatus from 'linced-app/enums/enrolment-status';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import {
  filterBy,
  findBy,
  mapBy,
  rejectBy,
} from '@linc-technologies/linced-common/utils/arrays';

export default class AttendanceGroupPopup extends Component {
  // Services
  @service
  attendanceCodes;

  @service
  attendanceMessages;

  @service
  dates;

  @service
  fetch;

  @service
  options;

  @service
  permissions;

  @service
  personOptions;

  @service
  session;

  @service
  store;

  @service
  strings;

  @service
  toaster;

  Jurisdiction = Jurisdiction;

  @alias('session.school.jurisdiction')
  jurisdiction;

  @computed('jurisdiction')
  get trackMovements() {
    return this.jurisdiction !== Jurisdiction.NZ_MOE;
  }

  @computed('jurisdiction')
  get labelField() {
    switch (this.jurisdiction) {
      case Jurisdiction.AU_NSW:
        return 'label';
    }
    return 'reason';
  }

  @computed('labelField', 'attendanceCodes.allCodes.[]')
  get codeLabelMap() {
    return this.attendanceCodes.allCodes.reduce((map, code) => {
      map[get(code, 'code')] = get(code, this.labelField);
      return map;
    }, {});
  }

  step = 1;

  @tracked
  learners = [];

  @alias('attendanceCodes.unknown.code')
  unknownCode;

  @optionAlias('core:attendance:attendanceCodeOnRoll')
  showCodesToTeachers;

  @computed('session.scopes.[]')
  get isAdmin() {
    return this.permissions.meet('urn:linced:organisation:role:admin');
  }

  @or('showCodesToTeachers', 'isAdmin')
  showCodes;

  @optionAlias('hero:features:weeklyAdminAttendance')
  enableWeekViewForSchool;

  @optionAlias('core:attendance:enableWeekViewForTeachers')
  enableWeekViewForTeachers;

  // enableWeekView determines whether to enable the whole-week view.
  @computed('enableWeekViewForSchool', 'enableWeekViewForTeachers', 'isAdmin')
  get enableWeekView() {
    return (
      this.enableWeekViewForSchool &&
      (this.enableWeekViewForTeachers || this.isAdmin)
    );
  }

  @computed('learners.@each.status')
  get presentLearners() {
    return this.learners.filter((learner) => {
      return learner.get('status.inClass');
    });
  }

  @computed('learners.@each.status')
  get numPresent() {
    return this.presentLearners.length;
  }

  @computed('learners.@each.status')
  get absentLearners() {
    return this.learners.filter((learner) => {
      return !learner.get('status.inClass');
    });
  }

  @computed('learners.{[],@each.status}')
  get numAbsent() {
    return this.absentLearners.length;
  }

  @computed('learners.@each.status')
  get markedLearners() {
    return this.learners.filter((learner) => {
      let code = learner.get('status.code') ?? '';
      return !isEmpty(code) && code !== this.unknownCode;
    });
  }

  @computed('markedLearners.[]')
  get numMarked() {
    return this.markedLearners.length;
  }

  // marksAreFinal determines whether already-marked students can be updated
  // from the group attendance popup.
  @computed('trackMovements', 'isAdmin')
  get marksAreFinal() {
    if (this.isAdmin) {
      return false;
    }
    return !this.trackMovements;
  }

  // isFirstRegister flags whether we think we're creating the first Register
  // for this group+period, or whether others exist already.
  @tracked
  isFirstRegister = false;

  // firstRegister is the first register for this group+period, if it's already
  // been marked.
  @tracked
  firstRegister = null;

  // canBulkRevise determines whether the option to bulk change/revise codes
  // should be made available.
  @computed('isAdmin', 'trackMovements', 'isFirstRegister')
  get canBulkRevise() {
    if (!this.isAdmin) {
      // Only Hero Administrators can bulk revise attendance codes.
      return false;
    }
    if (this.trackMovements) {
      // Attendance codes can only be bulk revised in schools which don't
      // track movements.
      return false;
    }
    // If it's the first register for this group+period, there's nothing to
    // revise (or clear) in bulk.
    return !this.isFirstRegister;
  }

  reviseCodesDialogOpen = false;

  @tracked
  bulkFromCode = '';

  @action
  setBulkFromCode(value) {
    this.bulkFromCode = value;
  }
  @tracked
  bulkToCode = '';

  @action
  setBulkToCode(value) {
    this.bulkToCode = value;
  }

  bulkClearDialogOpen = false;

  // potentialFutureAbsences provides a filtered view of markedLearners, only
  // including those who have attendance records predating the first Register
  // for this group+period.  Used to avoid future absences when bulk clearing
  // attendance codes.
  @computed('markedLearners.[]', 'firstRegister')
  get potentialFutureAbsences() {
    let firstRegisterCreated = this.get('firstRegister.createTime');
    if (isEmpty(firstRegisterCreated)) {
      // We don't have a first register to use as a reference point, or it's
      // missing the creation timestamp (so not usable for this purpose).
      return [];
    }

    // Apply a small "fudge factor" for Registers created with a slightly
    // later createTime than the related Records.
    firstRegisterCreated -= 5;

    return this.markedLearners.filter((learner) => {
      if (isEmpty(learner.record)) {
        // No record - can't have been a future absence.
        return false;
      }
      // Only include learners whose record was created prior to the first
      // Register for this group+period.
      return learner.get('record.createTime') < firstRegisterCreated;
    });
  }

  updateLearners() {
    let status = this.get('attendanceCodes.default');
    let isToday = moment().isSame(moment(this.date.date, 'YYYY-MM-DD'), 'day');
    let learnersPromise = Promise.resolve();

    if (isToday) {
      // Marking attendance for today.  Just use the current group membership, filtered to current students.
      // For transfer pre-enrolments from another Hero school, enrolmentStub follows enrolmentId which still points at the old
      // school's FORMER enrolment until PRE_ENROLLED→CURRENT fires (which happens at the start date).
      // Look up by schoolId first.
      let current = this.get('group.currentLearners').filter((member) => {
        let stub = member.get('person.learner.enrolmentStub');
        return !stub || stub.status !== EnrolmentStatus.PRE_ENROLLED;
      });
      learnersPromise = Promise.resolve(mapBy(current, 'person'));
    } else {
      // Marking attendance for a different date.  Check which students were current on this date.
      // First, find IDs of former students.
      let formerPersonIds = mapBy(
        filterBy(
          this.get('group.sortedLearners'),
          'person.learner.enrolmentStub.status',
          EnrolmentStatus.FORMER,
        ),
        'personId',
      );

      // Query for enrolment records for current students on that date, explicitly passing
      // personIds of former students as it uses current group members only by default.
      learnersPromise = this.store
        .query('people/enrolment', {
          currentOnDate: this.get('date.date'),
          groupId: this.get('group.id'),
          people: JSON.stringify(formerPersonIds),
          include: 'people',
        })
        .then((enrolments) => {
          return enrolments.reduce((people, enrolment) => {
            if (!findBy(people, 'id', enrolment.personId)) {
              people.push(this.store.peekRecord('person', enrolment.personId));
            }
            return people;
          }, []);
        });
    }

    learnersPromise.then((people) => {
      let learners = this.learners.filter((learner) =>
        findBy(people, 'id', learner.get('person.id')),
      );
      people.forEach((person) => {
        if (!findBy(learners, 'person.id', person.id)) {
          learners.push(
            EmberObject.create({
              person,
              status,
              marked: false,
            }),
          );
        }
      });
      this.learners = learners;
      // On the next runloop, check for any students whose attendance has already
      // been marked for this period on this day.
      next(this, 'checkMarkedLearners');
    });
  }

  @tracked
  newStatus = null;

  @tracked
  reasonLearner = null;

  @tracked
  reason = '';

  @action
  setReason(value) {
    this.reason = value;
  }

  @tracked
  effectiveFrom = '';

  @action
  setEffectiveFrom(value) {
    this.effectiveFrom = value;
  }

  @tracked
  effectiveFromDefaulted = false;

  @computed('period')
  get minTime() {
    if (!isEmpty(this.period)) {
      return this.get('period.startTime');
    }
    return '08:30';
  }

  @computed('date.date', 'minTime')
  get minMoment() {
    return moment(
      `${this.get('date.date')}T${this.minTime}`,
      'YYYY-MM-DDTHH:mm',
    );
  }

  @computed('period')
  get maxTime() {
    if (!isEmpty(this.period)) {
      return this.get('period.endTime');
    }
    return '15:30';
  }

  @computed('date.date', 'maxTime')
  get maxMoment() {
    return moment(
      `${this.get('date.date')}T${this.maxTime}`,
      'YYYY-MM-DDTHH:mm',
    );
  }

  @computed('date.date', 'minTime', 'maxTime')
  get effectiveFromValidation() {
    let minTime = this.minMoment.format('h:mm A');
    let maxTime = this.maxMoment.format('h:mm A');
    return [
      {
        message: `Time must be within the school period (${minTime} - ${maxTime}).`,
        validate: (val) => {
          if (isEmpty(val)) {
            return true;
          }
          let parsed = moment(
            `${this.get('date.date')}T${val}`,
            'YYYY-MM-DDTHH:mm',
          );
          return (
            parsed.isValid() &&
            parsed.isSameOrAfter(this.minMoment, 'm') &&
            parsed.isSameOrBefore(this.maxMoment, 'm')
          );
        },
      },
    ];
  }

  @tracked
  reasonOpen = false;

  @tracked
  markAllPresentDialogOpen = false;

  // markAllPresentLearners uses a native getter rather than a computed:
  // `status` is a nested object on each learner, so a two-level @each key is
  // not allowed and an intermediary CP would not see per-learner status
  // changes. Autotracking consumes each learner's tags as we iterate, so this
  // recomputes correctly.
  get markAllPresentLearners() {
    let offsiteCodes = mapBy(this.get('attendanceCodes.bulkOffsite'), 'code');
    return this.learners.filter((learner) => {
      if (!learner.marked && !learner.touched) {
        // Always include unmarked learners who haven't had a code assigned.
        return true;
      }
      if (offsiteCodes.includes(learner.get('status.code'))) {
        // When bulk marking Present, also include Offsite learners.
        // This is to be used when returning from an offsite activity.
        return true;
      }

      // Otherwise, don't include this learner.
      return false;
    });
  }

  @computed('attendanceCodes.bulkOffsite.[]')
  get newOffsiteCodes() {
    return filterBy(this.get('attendanceCodes.bulkOffsite'), 'old', false);
  }

  @computed('markAllPresentLearners.@each.effectiveFrom', 'effectiveFrom')
  get markAllPresentIsOverride() {
    return this.markAllPresentLearners.every((learner) => {
      return learner.effectiveFrom === this.effectiveFrom;
    });
  }

  @tracked
  saving = false;

  @tracked
  failed = false;

  AttendanceViewMode = AttendanceViewMode;

  // mode determines the view mode (period or week).
  @alias('personOptions.attendanceViewMode')
  defaultMode;

  @tracked
  mode = AttendanceViewMode.WEEK;

  // weeklyRecords contains the week's attendance records, if being shown.
  @tracked
  weeklyRecords = [];

  // weekDays contains a map of weekdays to date and periods.
  @computed('date.date')
  get weekDays() {
    let date = moment(this.date.date, 'YYYY-MM-DD');
    return [1, 2, 3, 4, 5].map((day) => {
      date.day(day);
      let formatted = date.format('YYYY-MM-DD');
      return {
        date: formatted,
        day: date.format('ddd'),
        shortDate: date.format('DD/MM'),
        periods: this.dates.dayForDate(formatted).periods,
      };
    });
  }

  // hasMultiplePeriodsPerDay calculates whether any day has multiple periods.
  @computed('weekDays.@each.periods')
  get hasMultiplePeriodsPerDay() {
    if (isArray(this.weekDays)) {
      return this.weekDays.some((day) => {
        return isArray(day.periods) && day.periods.length > 1;
      });
    }

    // No days to check.
    return false;
  }

  // sortBy determines the field to be sorted by when showing the whole week.
  sortBy = 'person.firstName';

  // sortDir determines the sort direction when showing the whole week.
  sortDir = 'asc';

  // weeklyMap maps the weekly records by person, date and period.
  @computed('weeklyRecords.[]')
  get weeklyMap() {
    return this.weeklyRecords.reduce((map, record) => {
      if (isEmpty(map[record.person_id])) {
        map[record.person_id] = {};
      }
      if (isEmpty(map[record.person_id][record.date])) {
        map[record.person_id][record.date] = {};
      }
      map[record.person_id][record.date][record.period_id] = record;

      return map;
    }, {});
  }

  // weeklyCounts calculates the number of students for each truancy status
  // within each period across the whole week.
  @computed(
    'date.date',
    'period.id',
    'weeklyRecords.[]',
    'truancyCodes',
    'learners.@each.status',
  )
  get weeklyCounts() {
    // Determine the valid learners, so we can skip any surplus records.
    let validLearnerIds = this.learners.reduce((ids, learner) => {
      ids[learner.get('person.id')] = true;
      return ids;
    }, {});
    // Initial count based on the week's attendance records.
    let counts = this.weeklyRecords.reduce((map, record) => {
      if (!validLearnerIds[record.person_id]) {
        // Not a learner whose records should be included.
        return map;
      }
      if (isEmpty(map[record.date])) {
        map[record.date] = {};
      }
      if (isEmpty(map[record.date][record.period_id])) {
        map[record.date][record.period_id] = {};
      }
      let truancyCode = this.truancyCodes[record.code];
      if (isEmpty(map[record.date][record.period_id][truancyCode])) {
        map[record.date][record.period_id][truancyCode] = 1;
      } else {
        map[record.date][record.period_id][truancyCode]++;
      }

      return map;
    }, {});

    // Replace weekly record count with current status counts for the active
    // period, to show "live" counts based on the user's changes.
    if (isEmpty(counts[this.date.date])) {
      counts[this.date.date] = {};
    }
    counts[this.date.date][this.period.id] = this.learners.reduce(
      (counts, learner) => {
        if (!isEmpty(learner.status)) {
          let truancyCode = this.truancyCodes[learner.status.code];
          if (isEmpty(counts[truancyCode])) {
            counts[truancyCode] = 1;
          } else {
            counts[truancyCode]++;
          }
        }
        return counts;
      },
      {},
    );

    return counts;
  }

  @computed('attendanceCodes.quickCodes.[]')
  get availableCodes() {
    return rejectBy(this.attendanceCodes.allCodes, 'old');
  }

  @computed('attendanceCodes.allCodes.[]')
  get truancyCodes() {
    return this.attendanceCodes.allCodes.reduce((map, code) => {
      map[code.code] = code.truancy;
      return map;
    }, {});
  }

  @alias('attendanceCodes.truancyClasses')
  truancyClasses;

  @tracked
  markAllDialogOpen;

  @tracked
  markAllCode;

  @action
  setMarkAllCode(value) {
    this.markAllCode = value;
  }

  @tracked
  markAllNotes;

  @action
  setMarkAllNotes(value) {
    this.markAllNotes = value;
  }

  // The last group, date and period that the dialog loaded.
  #loaded = {};

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);

    // The parent can send the same arguments again (for example each minute,
    // from the dates service). Do not reset the view and the unsaved marks
    // when the group, date and period did not change.
    let { group, date, period } = this;
    let loaded = this.#loaded;
    if (
      loaded.group === group &&
      loaded.date === date &&
      loaded.period === period
    ) {
      return;
    }
    loaded.group = group;
    loaded.date = date;
    loaded.period = period;

    // Go back to the marking step: the marks of a new period need a review
    // before they are saved. Next stays disabled until the learners load.
    this.set('step', 1);
    this.failed = false;
    this.mode = this.defaultMode;
    this.learners = [];
    this.weeklyRecords = [];

    // Trigger an async update of the group, so that we detect any membership changes
    // which have happened since the group was loaded.
    this.group.reload().then(() => {
      this.updateLearners();
    });
  }

  onSave() {}

  generateEffectiveFrom(record) {
    // effectiveFrom defaults to empty if the record isn't for today,
    // or if it's an NZ MoE school.
    let effectiveFrom = '';

    if (!isEmpty(record)) {
      let isToday = record.date === this.dates.currentDate;
      if (isToday && this.trackMovements) {
        effectiveFrom = moment().format('HH:mm');
      }
    }

    return {
      effectiveFrom,
      effectiveFromDefaulted: !isEmpty(effectiveFrom),
    };
  }

  @action
  clearBulkRevise() {
    this.bulkFromCode = '';
    this.bulkToCode = '';
    this.reason = '';
  }

  @action
  next() {
    // Confirm we have a fresh token when the user clicks “Next”
    this.session.getToken().then(() => {
      this.incrementProperty('step');
    });
  }

  @action
  back() {
    this.decrementProperty('step');
    this.failed = false;
  }

  @action
  getStatusClass(marked, status) {
    if (marked || (!isEmpty(status) && status.code !== this.unknownCode)) {
      return this.truancyClasses[status.code];
    }
    return '';
  }

  @action
  toggleAttendance(person) {
    let learner = findBy(this.learners, 'person.id', person.get('id'));
    let present = this.get('attendanceCodes.present');
    let unknown = this.get('attendanceCodes.default');
    if (learner) {
      let oldCode = learner.get('status.code');
      let status = present;
      if (oldCode === present.code) {
        status = unknown;
      }

      let markedNotUnknown =
        learner.marked && oldCode !== unknown.code && !isEmpty(oldCode);
      let lateArrival =
        learner.marked &&
        oldCode === unknown.code &&
        status.code === present.code;
      if (markedNotUnknown || (this.trackMovements && lateArrival)) {
        let { effectiveFrom, effectiveFromDefaulted } =
          this.generateEffectiveFrom(learner.record);
        this.reasonLearner = learner;
        this.newStatus = status;
        this.reason = '';
        this.effectiveFrom = effectiveFrom;
        this.effectiveFromDefaulted = effectiveFromDefaulted;
        this.reasonOpen = true;
      } else {
        learner.setProperties({
          status,
          touched: true,
          // Flag it as unmarked so that it doesn't get immediately frozen
          // if the student already had an "unknown" mark saved.
          marked: false,
        });
      }
    }
  }

  @action
  disabled() {} // no-op action for use when widget is disabled.

  @action
  markAllAs(status, notes) {
    let unknown = this.get('attendanceCodes.default.code');
    let learnersToMark = [];

    if (this.marksAreFinal) {
      // Bulk mark all unmarked/unknown learners.  Don't change anyone else.
      learnersToMark = this.learners.filter(
        (learner) => learner.get('status.code') === unknown,
      );
    } else {
      // Bulk marking can happen later in the period, for (i.e.) class trips.
      let offsiteCodes = mapBy(this.get('attendanceCodes.bulkOffsite'), 'code');
      learnersToMark = this.learners.filter((learner) => {
        if (!learner.marked && !learner.touched) {
          // Always include unmarked learners who haven't had a code assigned.
          return true;
        }
        if (
          status.inClass &&
          offsiteCodes.includes(learner.get('status.code'))
        ) {
          // When bulk marking Present, also include Offsite learners.
          // This is to be used when returning from an offsite activity.
          return true;
        }
        if (!status.inClass && learner.get('status.inClass')) {
          // When bulk marking Offsite, also include Present learners.
          // This is to be used when leaving for an offsite activity.
          return true;
        }

        // Otherwise, don't include this learner.
        return false;
      });
    }

    learnersToMark.forEach((learner) => {
      let props = {
        status,
        touched: true,
        effectiveFrom: this.effectiveFrom,
      };
      if (!isEmpty(notes)) {
        props.notes = notes;
        if (this.trackMovements) {
          props.reason = notes;
        }
      }
      learner.setProperties(props);
    });
    this.markAllDialogOpen = false;
  }

  @action
  setDefaultMode() {
    this.personOptions.setAttendanceViewMode(this.mode);
  }

  @action
  setStatus(learner, status) {
    let oldCode = learner.status.code;
    let present = this.get('attendanceCodes.present.code');
    let unknown = this.get('attendanceCodes.unknown.code');

    let changedNotUnknown =
      learner.marked &&
      oldCode !== status.code &&
      oldCode !== unknown &&
      !isEmpty(oldCode);
    let lateArrival =
      learner.marked && oldCode === unknown && status.code === present;
    if (changedNotUnknown || (this.trackMovements && lateArrival)) {
      let { effectiveFrom, effectiveFromDefaulted } =
        this.generateEffectiveFrom(learner.record);
      this.reasonLearner = learner;
      this.newStatus = status;
      this.reason = '';
      this.effectiveFrom = effectiveFrom;
      this.effectiveFromDefaulted = effectiveFromDefaulted;
      this.reasonOpen = true;
    } else {
      learner.setProperties({
        status,
        reason: '',
        touched: true,
        // Flag it as unmarked so that it doesn't get immediately frozen
        // if the student already had an "unknown" mark saved.
        marked: false,
      });
    }
  }

  @action
  setStatusWithReason() {
    let learner = this.reasonLearner;
    if (!isEmpty(learner)) {
      learner.setProperties({
        status: this.newStatus,
        reason: this.reason,
        notes: this.reason,
        effectiveFrom: this.effectiveFrom,
        touched: true,
      });
    }

    this.reasonLearner = null;
    this.newStatus = '';
    this.reason = '';
    this.effectiveFrom = '';
    this.effectiveFromDefaulted = false;
    this.reasonOpen = false;
  }

  @action
  clearReason() {
    this.reason = '';
  }

  @action
  closeReasonDialog() {
    this.reasonOpen = false;
  }

  @action
  save() {
    this.saving = true;
    this.failed = false;

    let periodId = this.get('period.id');
    let data = {
      register: {
        schoolId: this.get('session.activeSchool'),
        dateId: this.get('date.id'),
        date: this.get('date.date'),
        periodId,
        groupId: this.get('group.id'),
        authorId: this.get('session.personId'),
      },
      records: [],
    };

    this.learners.forEach((learner) => {
      let effectiveFrom;
      if (!isEmpty(learner.get('effectiveFrom'))) {
        effectiveFrom = moment(
          `${this.get('date.date')}T${learner.effectiveFrom}`,
          'YYYY-MM-DDTHH:mm',
        ).unix();
      }
      let record = {
        personId: learner.get('person.id'),
        code: learner.get('status.code'),
        notes: learner.get('notes'),
        reason: learner.get('reason'),
        authorId: this.get('session.person.id'),
        effectiveFrom,
      };
      if (this.trackMovements) {
        // Note: We'll always send the updated code/notes in the main record,
        // and let the back end determine whether to persist them.
        if (isEmpty(learner.record)) {
          // Since there's no record yet, assume that this applies from the
          // start of the period rather than current time.  The user wasn't
          // prompted to specify the time.
          effectiveFrom = moment(
            `${this.get('date.date')}T${this.get('period.startTime')}`,
            'YYYY-MM-DDTHH:mm',
          ).unix();
          record.effectiveFrom = effectiveFrom;
          record.movements = [
            {
              time: effectiveFrom,
              code: record.code,
              authorId: this.get('session.person.id'),
              notes: learner.notes,
            },
          ];
        } else {
          // Ignore any existing movements; the back end will reconcile the
          // new movement against existing ones.
          record.movements = [
            {
              time: effectiveFrom,
              code: record.code,
              authorId: this.get('session.person.id'),
              notes: learner.reason || learner.notes,
            },
          ];
        }
      }

      data.records.push(record);
    });

    let options = {
      data: JSON.stringify(data),
    };

    this.fetch
      .post(
        `${config.APP.apiHost}/attendance/${config.APP.apiPath}/registers`,
        options,
      )
      .then((response) => {
        this.store.pushPayload('attendance/register', {
          register: response.register,
        });
        this.store.pushPayload('attendance/record', {
          records: response.records,
        });
        this.saving = false;
        this.failed = false;
        this.onSave();
      })
      .catch(() => {
        this.toaster.error(`Error saving attendance data!`);
        this.saving = false;
        this.failed = true;
      });
  }

  @action
  openMarkAll() {
    let effectiveFrom;
    if (this.trackMovements) {
      if (this.isFirstRegister) {
        // First Register of the period for this group.
        // Default to start of period.
        effectiveFrom = this.get('period.startTime');
      } else {
        // Otherwise, default to now.
        effectiveFrom = moment().format('HH:mm');
      }
    }
    let markAllCode;
    switch (this.jurisdiction) {
      case Jurisdiction.AU_NSW:
        // NSW only has one offsite code; default to it to save clicks.
        markAllCode = this.attendanceCodes.bulkOffsite[0];
    }
    this.markAllDialogOpen = true;
    this.markAllCode = markAllCode;
    this.markAllNotes = '';
    this.effectiveFrom = effectiveFrom;
    this.effectiveFromDefaulted = !isEmpty(effectiveFrom);
  }

  @action
  closeMarkAll() {
    this.markAllDialogOpen = false;
  }

  @action
  markAllPresent() {
    if (this.trackMovements && !this.isFirstRegister) {
      // We're tracking Movements, and already have a Register for this
      // period.  Assume students are returning to class; default the time
      // to now.
      let effectiveFrom = moment().format('HH:mm');
      this.markAllPresentDialogOpen = true;
      this.effectiveFrom = effectiveFrom;
      this.effectiveFromDefaulted = true;
      return;
    }

    // No movement tracking, or this is the first mark of the period.
    // Bulk mark all unmarked/unknown learners.  Don't change anyone else.
    let unknown = this.get('attendanceCodes.default.code');
    let learnersToMark = this.learners.filter(
      (learner) => learner.get('status.code') === unknown,
    );
    let effectiveFrom;
    if (this.trackMovements) {
      effectiveFrom = this.get('period.startTime');
    }
    learnersToMark.forEach((learner) => {
      let props = {
        status: this.get('attendanceCodes.present'),
        touched: true,
        effectiveFrom,
      };
      learner.setProperties(props);
    });
  }

  @action
  doMarkAllPresent() {
    this.markAllPresentLearners.forEach((learner) => {
      let props = {
        status: this.get('attendanceCodes.present'),
        touched: true,
        effectiveFrom: this.effectiveFrom,
        notes: '',
        reason: '',
      };
      if (this.markAllPresentIsOverride) {
        // Specify the audit reason if the user is changing a previous bulk
        // mark, as they're likely correcting a mistake.  The back end will
        // apply the reason as the movement notes (and record, if applicable)
        // so we should mimic that here to avoid showing a different state.
        props.reason = this.reason;
        props.notes = this.reason;
      }
      learner.setProperties(props);
    });
    this.markAllPresentDialogOpen = false;
  }

  @action
  closeMarkAllPresent() {
    this.markAllPresentDialogOpen = false;
  }

  @action
  bulkClearCodes() {
    let dateId = this.get('date.id');
    let periodId = this.get('period.id');
    let params = {
      schoolId: this.get('session.activeSchool'),
      dateId,
      periodId,
      groupId: this.get('group.id'),
    };
    let reason = this.reason;

    this.store.query('attendance/register', params).then((registers) => {
      let deletes = registers.map((register) => {
        return register.destroyRecord();
      });
      deletes.concat(
        this.learners
          .filter((learner) => !this.potentialFutureAbsences.includes(learner))
          .map((learner) => {
            let props = {
              status: this.attendanceCodes.default,
              touched: false,
              marked: false,
            };
            learner.setProperties(props);
            learner.record.setProperties({
              authorId: this.get('session.person'),
              code: '',
              note: '',
              reason,
              auditReason: reason,
            });
            return learner.record.destroyRecord();
          }),
      );
      Promise.all(deletes)
        .then(() => {
          this.toaster.success('Attendance records cleared.');
          this.onSave();
        })
        .catch(() => {
          this.toaster.error('Attendance records failed to clear.');
          this.onSave();
        });
    });
  }

  @action
  bulkReviseCodes() {
    let learnersToMark = this.learners.filter(
      (learner) => learner.get('status.code') === this.bulkFromCode.code,
    );
    learnersToMark.forEach((learner) => {
      let props = {
        status: this.bulkToCode,
        touched: true,
        reason: this.reason,
        notes: this.reason,
      };
      learner.setProperties(props);
    });
    this.toggleProperty('reviseCodesDialogOpen');
    this.clearBulkRevise();
  }

  @action
  setViewMode(newMode) {
    this.mode = newMode;
  }

  checkMarkedLearners() {
    // Reset all learners' statuses and marked flags.
    let defaultStatus = this.get('attendanceCodes.default');
    this.learners.forEach((learner) => {
      learner.setProperties({
        status: defaultStatus,
        record: null,
        marked: false,
        touched: false,
        notes: '',
        reason: '',
        effectiveFrom: '',
      });
    });

    let dateId = this.get('date.id');
    let periodId = this.get('period.id');
    let params = {
      schoolId: this.get('session.activeSchool'),
      dateId,
      periodId,
      groupId: this.get('group.id'),
    };

    // Check whether there are any existing Registers for this group+period.
    this.store.query('attendance/register', params).then((registers) => {
      this.isFirstRegister = registers.length === 0;

      this.firstRegister = registers
        .slice()
        .sort((a, b) => a.createTime - b.createTime)[0];
    });

    // Expand query to the week if we're showing the whole week.
    if (this.enableWeekView) {
      let date = moment(this.date.date, 'YYYY-MM-DD');
      params = {
        schoolId: params.schoolId,
        groupId: params.groupId,
        dateFrom: date.day(1).format('YYYY-MM-DD'),
        dateTo: date.day(5).format('YYYY-MM-DD'),
      };
    }

    // Fetch all individual attendance records for the group's members
    // for the given date/period.
    this.store.query('attendance/record', params).then((records) => {
      let codes = this.get('attendanceCodes.allCodes');

      // Store weekly codes and reduce to the period's records if applicable.
      if (this.enableWeekView) {
        this.weeklyRecords = records;
        records = records.filter((record) => {
          return record.date_id === dateId && record.period_id === periodId;
        });
      }

      // Update learners' statuses and marked flags based on returned records.
      records.forEach((record) => {
        let learner = findBy(
          this.learners,
          'person.id',
          record.get('person.id'),
        );
        if (learner) {
          let code = record.get('code');
          let status = findBy(codes, 'code', code);
          if (status) {
            let effectiveFrom;
            if (this.trackMovements && !isEmpty(record.movements)) {
              // Default effectiveFrom to the last known movement, to prevent a
              // new movement from being generated by the back end.
              effectiveFrom = moment
                .unix(record.get('movements.lastObject.time'))
                .format('HH:mm');
            }
            learner.setProperties({
              status,
              record,
              marked: true,
              effectiveFrom,
            });
          }
          learner.set('notes', record.get('notes'));
        }
      });
    });

    // Also add any messages from caregivers
    let personIds = this.learners.map((learner) => {
      return learner.get('person.id');
    });
    this.attendanceMessages
      .queryDay(personIds, this.get('date.date'))
      .then((messages) => {
        this.learners.forEach((learner) => {
          let personId = learner.get('person.id');
          learner.set(
            'messages',
            messages.filter((message) => message.get('person.id') === personId),
          );
        });
      });
  }
}
